/**
 * AgendaRescheduleDialog — pick a new date (+ optional time + note) and
 * fire ``POST /agenda/{id}/reschedule``. The backend marks the original
 * as "reagendado" and creates a copy on the new day with the trail
 * "veio de 17/10 (reagendado)" so admin never loses history.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RefreshCcw } from "lucide-react";

const tomorrowIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function AgendaRescheduleDialog({ item, onClose, onDone }) {
  const [date, setDate] = useState(tomorrowIso());
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setDate(tomorrowIso());
      setTime(item.time || "");
      setNote("");
      setSaving(false);
    }
  }, [item]);

  const submit = async () => {
    if (!date) return toast.error("Escolha a nova data");
    setSaving(true);
    try {
      await api.post(`/agenda/${item.id}/reschedule`, {
        new_date: date,
        new_time: time || null,
        note: note.trim() || null,
      });
      toast.success(`Reagendado para ${new Date(date + "T12:00:00").toLocaleDateString("pt-BR")}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao reagendar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="agenda-reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCcw className="w-5 h-5 text-amber-700" />
            Reagendar
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Vou criar uma cópia na nova data e marcar o item atual como <strong>reagendado</strong>.
            O histórico fica preservado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {item?.title && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-[12px]">
              <div className="font-semibold text-slate-700">{item.title}</div>
              <div className="text-[10.5px] text-slate-500">
                Hoje: {new Date(item.date + "T12:00:00").toLocaleDateString("pt-BR")}
                {item.time ? ` às ${item.time}` : ""}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-2">
            <div className="min-w-0">
              <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Nova data</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="reschedule-date"
                className="h-10 w-full min-w-0 block appearance-none text-left"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Hora</label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="reschedule-time"
                className="h-10 w-full min-w-0 block appearance-none text-left"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-black uppercase text-slate-600 mb-1">Motivo (opcional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Ex: Diretora pediu pra mudar"
              data-testid="reschedule-note"
              className="text-xs resize-none"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={onClose} disabled={saving} className="flex-1">
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="reschedule-save"
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
          >
            {saving ? "Reagendando..." : "Reagendar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
